import { useDebouncedState } from '../helpers/hooks';
import { QueryDefinition } from '@reduxjs/toolkit/dist/query';
import {
  UseQuery,
  LazyQueryTrigger,
} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { useCallback, useEffect, useState } from 'react';

export enum PaginationType {
  Cursor = 'cursor',
  Offset = 'offset',
}

export const usePaginatedResource = <
  T extends unknown,
  P extends Record<string, unknown>,
>(
  query: UseQuery<QueryDefinition<Partial<P>, any, any, T[]>>,
  filters: Partial<P>,
  search: string,
  options?: {
    pageSize?: number;
    paginationType?: PaginationType;
    cursorExtractor?: (
      oldCursor: string | null,
      responseData: T[] | undefined,
    ) => string | null;
  },
  lazyQuery?: LazyQueryTrigger<QueryDefinition<Partial<P>, any, any, T[]>>,
): {
  data: T[] | undefined;
  isLoading: boolean;
  isFetching: boolean;
  exhausted: boolean;
  next: () => void;
  reset: () => void;
  refresh: () => Promise<void>;
} => {
  const {
    cursorExtractor,
    pageSize = 30,
    paginationType = PaginationType.Offset,
  } = options ?? {};

  const [shouldFetch, setShouldFetch] = useState(true);
  const [exhausted, setExhausted] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [data, setData] = useState<T[]>([]);
  const [debouncedSearch, setDebouncedSearch] = useDebouncedState(search, 500);

  useEffect(() => {
    setDebouncedSearch(search);
  }, [search]);

  const {
    data: responseData,
    isLoading,
    isFetching,
    endpointName,
  } = query(
    {
      ...filters,
      limit: pageSize,
      offset: cursor ?? '',
      search: debouncedSearch,
    },
    { skip: !shouldFetch },
  );

  const _updateCursor = (
    response: T[] | undefined,
    resetOffset: boolean = false,
  ) => {
    if (response && response.length > 0) {
      if (paginationType === PaginationType.Offset) {
        setCursor((prevCursor) => {
          const prevOffset =
            resetOffset || !prevCursor ? 0 : parseInt(prevCursor);
          return (prevOffset + response.length).toString();
        });
      } else if (paginationType === PaginationType.Cursor) {
        if (!cursorExtractor) {
          throw new Error(
            'Cursor extractor function must be provided for cursor pagination',
          );
        }
        setCursor((prevCursor) => cursorExtractor(prevCursor, response));
      }
    }
  };

  if (shouldFetch && isFetching === false && exhausted === false) {
    setData((prevComponents) =>
      responseData ? [...prevComponents, ...responseData] : prevComponents,
    );
    setShouldFetch(false);
    _updateCursor(responseData);
    if (responseData && responseData.length < pageSize) {
      setExhausted(true);
    }
  }

  const next = useCallback(() => {
    if (!exhausted && !isFetching) setShouldFetch(true);
  }, [setShouldFetch, exhausted, isFetching]);

  const reset = useCallback(() => {
    setShouldFetch(true);
    setExhausted(false);
    setData([]);
    setCursor(null);
  }, [setShouldFetch, setExhausted, setData, setCursor]);

  const refresh = useCallback(async () => {
    if (!lazyQuery) {
      console.warn(
        `Lazy query for ${endpointName} is not provided. Refresh will not work for this resource.`,
      );
      return;
    }
    try {
      const response = await lazyQuery({
        ...filters,
        limit: data.length,
        offset: null,
        search: debouncedSearch,
      });
      if (response.error) {
        throw response.error;
      }
      const refreshedData = response.data ?? [];
      setData(refreshedData);
      _updateCursor(refreshedData, true);
    } catch (error) {
      console.error(`Failed to refresh ${endpointName}:`, error);
    }
  }, [
    lazyQuery,
    filters,
    data.length,
    debouncedSearch,
    pageSize,
    _updateCursor,
  ]);

  useEffect(() => {
    reset();
  }, [debouncedSearch, filters, paginationType, pageSize]);

  return {
    data,
    isLoading,
    isFetching,
    exhausted,
    next,
    reset,
    refresh,
  };
};
